import React from 'react'
import { tv } from 'tailwind-variants'

// The table's markup, in the manner of shadcn/ui's table: 13px text in the body colour throughout, a
// medium-weight header row, rows parted by hairlines that lift on hover, and the first and last cells
// 16px in, so a table set in a card lines up with the card's title. Only the look lives here; DataTable
// fills them from its rows and columns.
const table = tv({
  slots: {
    container: 'relative w-full overflow-x-auto',
    root: 'w-full caption-bottom border-collapse text-[13px] text-body',
    header: '[&_tr]:border-b [&_tr]:border-divider',
    body: '[&_tr:last-child]:border-0',
    row: 'border-b border-divider transition-colors hover:bg-surface-hover/50 data-[state=selected]:bg-blue/5',
    head: 'h-9 px-3 first:pl-4 last:pr-4 text-left align-middle font-medium text-body whitespace-nowrap',
    cell: 'h-11 px-3 first:pl-4 last:pr-4 align-middle whitespace-nowrap',
    caption: 'mt-4 text-[13px] text-muted'
  },
  variants: {
    // A header that stays in view while the rows scroll under it, on the table's own background. Its line
    // underneath is drawn as a shadow: a sticky cell in a collapsed-border table loses its border.
    sticky: {
      true: {
        header:
          '[&_th]:sticky [&_th]:top-0 [&_th]:z-[1] [&_th]:bg-surface [&_th]:shadow-[inset_0_-1px_0_var(--color-divider)]'
      }
    }
  }
})

const styles = table()

export const Table: React.FC<React.TableHTMLAttributes<HTMLTableElement>> = ({ className, ...props }) => (
  <div data-slot="table-container" className={styles.container()}>
    <table data-slot="table" className={styles.root({ class: className })} {...props} />
  </div>
)

export const TableHeader: React.FC<React.HTMLAttributes<HTMLTableSectionElement> & { sticky?: boolean }> = ({
  className,
  sticky,
  ...props
}) => <thead data-slot="table-header" className={table({ sticky }).header({ class: className })} {...props} />

export const TableBody: React.FC<React.HTMLAttributes<HTMLTableSectionElement>> = ({ className, ...props }) => (
  <tbody data-slot="table-body" className={styles.body({ class: className })} {...props} />
)

export const TableRow: React.FC<React.HTMLAttributes<HTMLTableRowElement>> = ({ className, ...props }) => (
  <tr data-slot="table-row" className={styles.row({ class: className })} {...props} />
)

export const TableHead: React.FC<React.ThHTMLAttributes<HTMLTableCellElement>> = ({ className, ...props }) => (
  <th data-slot="table-head" className={styles.head({ class: className })} {...props} />
)

export const TableCell: React.FC<React.TdHTMLAttributes<HTMLTableCellElement>> = ({ className, ...props }) => (
  <td data-slot="table-cell" className={styles.cell({ class: className })} {...props} />
)

export const TableCaption: React.FC<React.HTMLAttributes<HTMLTableCaptionElement>> = ({ className, ...props }) => (
  <caption data-slot="table-caption" className={styles.caption({ class: className })} {...props} />
)
